import React, { useState, useEffect, useMemo } from 'react';
import { Mail, Send, Inbox, Trash2, Search, Calendar, User, MessageSquare, X, Maximize2, CheckCircle } from 'lucide-react';
import api from '../api';
import MessageDetails from './MessageDetails';
import MessageModal from './MessageModal';

const MessageDashboard = ({ onClose, userRole }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState('received');
    const currentUserEmail = sessionStorage.getItem('userEmail');

    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [filterRecipient, setFilterRecipient] = useState('');
    const [selectedMessage, setSelectedMessage] = useState(null);

    const [contacts, setContacts] = useState([]);
    const [showNewMessageModal, setShowNewMessageModal] = useState(false);

    useEffect(() => {
        fetchMessages();
        fetchContacts();
    }, []);

    const fetchMessages = async () => {
        setLoading(true);
        try {
            const res = await api.get('messages/');
            setMessages(res.data);
        } catch (err) {
            console.error("Error fetching messages:", err);
        } finally {
            setLoading(false);
        }
    };

    const fetchContacts = async () => {
        try {
            const res = await api.get('users/contacts/');
            setContacts(res.data);
        } catch (err) {
            console.error("Error fetching contacts:", err);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm("¿Estás seguro de que deseas eliminar este mensaje?")) return;
        try {
            await api.delete(`messages/${id}/`);
            setMessages(messages.filter(m => m.id !== id));
        } catch (err) {
            console.error("Error deleting message:", err);
            alert("No se pudo eliminar el mensaje.");
        }
    };

    const toggleRead = async (msg) => {
        try {
            const action = msg.is_read ? 'mark-unread' : 'mark-read';
            await api.post(`messages/${msg.id}/${action}/`);
            setMessages(messages.map(m => m.id === msg.id ? { ...m, is_read: !m.is_read } : m));
        } catch (err) {
            console.error("Error toggling read status:", err);
        }
    };

    const filteredMessages = useMemo(() => {
        return messages.filter(m => {
            const isReceived = m.recipient_id === currentUserEmail || m.recipient_type === 'evento' || (m.recipient_type === 'materia' && m.materia_associated);
            const isSent = m.sender_email === currentUserEmail;

            if (view === 'received' && !isReceived) return false;
            if (view === 'sent' && !isSent) return false;
            if (dateFrom && new Date(m.date) < new Date(dateFrom)) return false;
            if (dateTo && new Date(m.date) > new Date(dateTo)) return false;
            if (searchTerm) {
                const s = searchTerm.toLowerCase();
                return (
                    m.subject.toLowerCase().includes(s) ||
                    m.text.toLowerCase().includes(s) ||
                    m.sender_name?.toLowerCase().includes(s) ||
                    m.sender_email?.toLowerCase().includes(s) ||
                    m.recipient_id?.toLowerCase().includes(s)
                );
            }
            return true;
        }).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    }, [messages, view, filterRecipient, dateFrom, dateTo, searchTerm, currentUserEmail]);

    const hasFilters = dateFrom || dateTo || filterRecipient || searchTerm;

    return (
        <div className="modal-overlay" onClick={onClose} style={{ padding: '1rem', zIndex: 1050 }}>
            <div
                className="modal-content glassmorphism animate-fade-in d-flex flex-column p-3 p-md-4"
                style={{ width: '98%', maxWidth: '1400px', height: '90vh', borderRadius: '20px' }}
                onClick={e => e.stopPropagation()}
            >
                {/* ── Header ── */}
                <div className="border-bottom border-secondary border-opacity-25 pb-3 mb-3">
                    {/* Fila 1: título + cerrar */}
                    <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center gap-2">
                            <MessageSquare className="text-primary" size={22} />
                            <h3 className="mb-0 section-title" style={{ fontSize: '1.15rem' }}>Buzón de Mensajes</h3>
                        </div>
                        <button type="button" className="btn-close-premium" onClick={onClose}>
                            <X size={18} />
                        </button>
                    </div>
                    {/* Fila 2: tabs + botón nuevo mensaje */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <div className="btn-group border border-secondary border-opacity-25 rounded p-1">
                            <button
                                onClick={() => setView('received')}
                                className={`btn btn-sm ${view === 'received' ? 'btn-primary' : 'btn-outline-secondary border-0'}`}
                            >
                                <Inbox size={15} className="me-1" /> Recibidos
                            </button>
                            <button
                                onClick={() => setView('sent')}
                                className={`btn btn-sm ${view === 'sent' ? 'btn-primary' : 'btn-outline-secondary border-0'}`}
                            >
                                <Send size={15} className="me-1" /> Enviados
                            </button>
                        </div>
                        <button
                            onClick={() => setShowNewMessageModal(true)}
                            className="btn btn-sm btn-success d-flex align-items-center gap-2 px-3 shadow-sm"
                            style={{ borderRadius: '20px' }}
                        >
                            <Send size={15} /> <span className="fw-bold">Nuevo Mensaje</span>
                        </button>
                    </div>
                </div>

                {/* ── Filtros ── */}
                <div className="d-flex flex-column gap-2 mb-3">
                    {/* Búsqueda */}
                    <div className="position-relative">
                        <Search className="position-absolute top-50 translate-middle-y ms-3 text-muted" size={16} />
                        <input
                            type="text"
                            className="form-control ps-5 border-secondary text-white"
                            style={{ background: 'rgba(255,255,255,0.08)' }}
                            placeholder="Buscar contenido, remitente..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>
                    {/* Fechas: una debajo de la otra en mobile, en fila en desktop */}
                    <div className="d-flex flex-column flex-md-row gap-2">
                        <div className="input-group flex-grow-1">
                            <span className="input-group-text border-secondary text-muted small" style={{ background: 'rgba(255,255,255,0.08)' }}>Desde</span>
                            <input
                                type="date"
                                className="form-control border-secondary text-white"
                                style={{ background: 'rgba(255,255,255,0.08)', colorScheme: 'dark' }}
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                            />
                        </div>
                        <div className="input-group flex-grow-1">
                            <span className="input-group-text border-secondary text-muted small" style={{ background: 'rgba(255,255,255,0.08)' }}>Hasta</span>
                            <input
                                type="date"
                                className="form-control border-secondary text-white"
                                style={{ background: 'rgba(255,255,255,0.08)', colorScheme: 'dark' }}
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                            />
                        </div>
                        {view === 'sent' && (
                            <div className="input-group flex-grow-1">
                                <span className="input-group-text border-secondary text-muted" style={{ background: 'rgba(255,255,255,0.08)' }}><Send size={14} /></span>
                                <select
                                    className="form-select border-secondary text-white"
                                    style={{ background: 'rgba(255,255,255,0.08)' }}
                                    value={filterRecipient}
                                    onChange={(e) => setFilterRecipient(e.target.value)}
                                >
                                    <option value="">Cualquier Destinatario</option>
                                    {contacts.filter(c => c.email !== currentUserEmail).map(c => (
                                        <option key={c.id} value={c.email}>{c.first_name || c.email}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>
                    {/* Limpiar filtros */}
                    {hasFilters && (
                        <button
                            className="btn btn-outline-danger btn-sm d-flex align-items-center gap-2 align-self-start"
                            onClick={() => { setFilterRecipient(''); setDateFrom(''); setDateTo(''); setSearchTerm(''); }}
                        >
                            <X size={14} /> Limpiar filtros
                        </button>
                    )}
                </div>

                {/* ── Lista de mensajes o detalle ── */}
                <div className="flex-grow-1 overflow-auto pe-1">
                    {loading ? (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 opacity-50">
                            <div className="spinner-border text-primary mb-3" role="status"></div>
                            <p>Cargando mensajes...</p>
                        </div>
                    ) : selectedMessage ? (
                        <div className="p-1 animate-fade-in h-100">
                            <MessageDetails
                                message={selectedMessage}
                                userRole={userRole}
                                onClose={(refresh) => {
                                    if (refresh) fetchMessages();
                                    setSelectedMessage(null);
                                }}
                            />
                        </div>
                    ) : filteredMessages.length > 0 ? (
                        <div className="d-flex flex-column gap-3">
                            {filteredMessages.map(msg => (
                                <div key={msg.id} className="card glassmorphism border-secondary border-opacity-25 hover-glow transition-all">
                                    <div className="card-body p-3">
                                        {/* Fila 1: badge + asunto */}
                                        <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                            <span className={`badge ${msg.sender_email === currentUserEmail ? 'bg-info text-dark' : 'bg-success'} fw-bold`}>
                                                {msg.sender_email === currentUserEmail ? 'ENVIADO' : 'RECIBIDO'}
                                            </span>
                                            {!msg.is_read && view === 'received' && (
                                                <div className="bg-primary rounded-circle" style={{ width: '9px', height: '9px', flexShrink: 0 }} title="No leído" />
                                            )}
                                            <h6 className="fw-bold text-primary mb-0 flex-grow-1" style={{ wordBreak: 'break-word' }}>
                                                {msg.subject}
                                                {msg.survey_id && <span className="badge bg-warning text-dark ms-2" style={{ fontSize: '0.6rem' }}>ENCUESTA</span>}
                                            </h6>
                                        </div>
                                        {/* Fila 2: remitente + fecha */}
                                        <div className="text-muted small d-flex flex-column gap-1 mb-2">
                                            <span className="d-flex align-items-center gap-1">
                                                <User size={13} />
                                                {msg.sender_email === currentUserEmail
                                                    ? `Para: ${msg.recipient_id || 'Todos'}`
                                                    : `De: ${msg.sender_name || msg.sender_email}`}
                                            </span>
                                            <span className="d-flex align-items-center gap-1">
                                                <Calendar size={13} /> {msg.date}
                                            </span>
                                        </div>
                                        {/* Fila 3: texto del mensaje */}
                                        <div className="text-start mb-3" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {msg.text}
                                        </div>
                                        {/* Adjunto PDF */}
                                        {msg.pdf_url && (
                                            <div className="mb-3 p-2 rounded bg-light border d-flex align-items-center justify-content-between flex-wrap gap-2">
                                                <div className="d-flex align-items-center gap-2 text-dark small">
                                                    <div className="bg-danger bg-opacity-10 p-2 rounded">
                                                        <Mail className="text-danger" size={18} />
                                                    </div>
                                                    <div>
                                                        <div className="fw-bold text-dark">Comprobante Adjunto</div>
                                                        <div className="text-muted opacity-75" style={{ fontSize: '0.75rem' }}>Archivo PDF</div>
                                                    </div>
                                                </div>
                                                <a
                                                    href={msg.pdf_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                                >
                                                    <Maximize2 size={13} /> Ver Comprobante
                                                </a>
                                            </div>
                                        )}
                                        {/* Encuesta */}
                                        {msg.survey_id && (
                                            <div className="mb-3 p-3 rounded bg-dark bg-opacity-50 border border-warning border-opacity-25">
                                                <h6 className="fw-bold text-warning mb-2 d-flex align-items-center gap-2">
                                                    <MessageSquare size={15} /> Encuesta Relacionada
                                                </h6>
                                                <button className="btn btn-sm btn-warning">Contestar / Ver Resultados</button>
                                            </div>
                                        )}
                                        {/* Fila 4: botones de acción — siempre en fila, wrap si no caben */}
                                        <div className="d-flex flex-wrap gap-2 pt-2 border-top">
                                            <button
                                                onClick={() => setSelectedMessage(msg)}
                                                className="btn btn-sm btn-primary d-flex align-items-center gap-2 px-3 shadow-sm border-0 flex-grow-1"
                                                style={{ borderRadius: '20px' }}
                                            >
                                                <MessageSquare size={15} />
                                                <span className="fw-bold">Ver / Responder</span>
                                            </button>
                                            {view === 'received' && (
                                                <button
                                                    onClick={() => toggleRead(msg)}
                                                    className={`btn btn-sm ${msg.is_read ? 'btn-outline-secondary' : 'btn-primary'}`}
                                                    title={msg.is_read ? "Marcar como no leído" : "Marcar como leído"}
                                                    style={{ borderRadius: '20px' }}
                                                >
                                                    {msg.is_read ? <Mail size={15} /> : <CheckCircle size={15} />}
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDelete(msg.id)}
                                                className="btn btn-sm btn-action reject"
                                                title="Eliminar mensaje"
                                                style={{ borderRadius: '20px' }}
                                            >
                                                <Trash2 size={15} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="d-flex flex-column align-items-center justify-content-center py-5 opacity-50">
                            <Mail size={48} className="mb-3" />
                            <p className="fs-5">No se encontraron mensajes con estos criterios.</p>
                        </div>
                    )}
                </div>

                {/* Modal nuevo mensaje */}
                {showNewMessageModal && (
                    <div className="modal-overlay" onClick={() => setShowNewMessageModal(false)} style={{ padding: '1rem', zIndex: 11000 }}>
                        <div
                            className="modal-content glassmorphism animate-fade-in p-3 p-md-4"
                            style={{ width: '98%', maxWidth: '1400px', height: '90vh', borderRadius: '20px', overflowY: 'auto' }}
                            onClick={e => e.stopPropagation()}
                        >
                            <MessageModal
                                date={new Date()}
                                onClose={(refresh) => {
                                    setShowNewMessageModal(false);
                                    if (refresh) fetchMessages();
                                }}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MessageDashboard;
